import { fetchWithAuth } from './apiUtils';
import { getAuthToken } from './authService';
import { API_URL } from '../constants/api';

const API_BASE_URL = `${API_URL}/devices`;

/**
 * Servicio para gestión de dispositivos
 */
const deviceService = {
  /**
   * Obtener todos los dispositivos con paginación
   * @param {Object} options - Opciones de consulta
   * @param {number} options.page - Número de página (default: 1)
   * @param {number} options.limit - Elementos por página (default: 10)
   * @param {string} options.search - Término de búsqueda
   * @param {string} options.device_type - Filtrar por tipo de dispositivo
   * @param {string} options.search_type - Tipo de búsqueda: 'device' o 'contact'
   * @returns {Promise} - Promesa con la lista de dispositivos paginada
   */
  async getAllDevices(options = {}) {
    const token = getAuthToken();
    
    // Construir parámetros de consulta
    const queryParams = new URLSearchParams();
    if (options.page) queryParams.append('page', options.page);
    if (options.limit) queryParams.append('limit', options.limit);
    if (options.search) queryParams.append('search', options.search);
    if (options.device_type) queryParams.append('device_type', options.device_type);
    if (options.search_type) queryParams.append('search_type', options.search_type);
    
    const url = queryParams.toString() ? `${API_BASE_URL}?${queryParams}` : API_BASE_URL;
    return await fetchWithAuth(url, {}, token);
  },

  /**
   * Obtener estadísticas de dispositivos
   * @returns {Promise} - Promesa con las estadísticas
   */
  async getDeviceStats() {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/stats`, {}, token);
  },

  /**
   * Obtener un dispositivo por ID
   * @param {string} id - ID del dispositivo
   * @returns {Promise} - Promesa con el dispositivo
   */
  async getDeviceById(id) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${id}`, {}, token);
  },

  /**
   * Crear un nuevo dispositivo
   * @param {Object} deviceData - Datos del dispositivo
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async createDevice(deviceData) {
    const token = getAuthToken();
    return await fetchWithAuth(API_BASE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(deviceData),
    }, token);
  },

  /**
   * Actualizar un dispositivo
   * @param {string} id - ID del dispositivo
   * @param {Object} deviceData - Datos actualizados del dispositivo
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async updateDevice(id, deviceData) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(deviceData),
    }, token);
  },

  /**
   * Eliminar un dispositivo
   * @param {string} id - ID del dispositivo
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async deleteDevice(id) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${id}`, {
      method: 'DELETE',
    }, token);
  },

  /**
   * Obtener contactos de un dispositivo
   * @param {string} deviceId - ID del dispositivo
   * @returns {Promise} - Promesa con los contactos del dispositivo
   */
  async getDeviceContacts(deviceId) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${deviceId}/contacts`, {}, token);
  },

  /**
   * Obtener contactos asociados a un dispositivo (suscripciones)
   * @param {string} deviceId - ID del dispositivo
   * @returns {Promise} - Promesa con los contactos del dispositivo
   */
  async getDeviceContactSubscriptions(deviceId) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${deviceId}/subscriptions`, {}, token);
  },

  /**
   * Actualizar contactos asociados a un dispositivo (suscripciones)
   * @param {string} deviceId - ID del dispositivo
   * @param {Array} contactIds - IDs de los contactos
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async updateDeviceContactSubscriptions(deviceId, contactIds) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${deviceId}/subscriptions`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contactIds }),
    }, token);
  },

  /**
   * Agregar contactos a un dispositivo
   * @param {string} deviceId - ID del dispositivo
   * @param {Array} contactIds - IDs de los contactos a agregar
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async addContactsToDevice(deviceId, contactIds) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${deviceId}/contacts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contact_ids: contactIds }),
    }, token);
  },

  /**
   * Remover contactos de un dispositivo
   * @param {string} deviceId - ID del dispositivo
   * @param {Array} contactIds - IDs de los contactos a remover
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async removeContactsFromDevice(deviceId, contactIds) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${deviceId}/contacts`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contact_ids: contactIds }),
    }, token);
  },

  /**
   * Obtener plantillas de un dispositivo
   * @param {string} deviceId - ID del dispositivo
   * @returns {Promise} - Promesa con las plantillas del dispositivo
   */
  async getDeviceTemplates(deviceId) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${deviceId}/templates`, {}, token);
  },

  /**
   * Crear plantilla para un dispositivo
   * @param {string} deviceId - ID del dispositivo
   * @param {Object} templateData - Datos de la plantilla
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async createDeviceTemplate(deviceId, templateData) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${deviceId}/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(templateData),
    }, token);
  },

  /**
   * Actualizar plantilla de un dispositivo
   * @param {string} deviceId - ID del dispositivo
   * @param {string} templateId - ID de la plantilla
   * @param {Object} templateData - Datos actualizados de la plantilla
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async updateDeviceTemplate(deviceId, templateId, templateData) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${deviceId}/templates/${templateId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(templateData),
    }, token);
  },

  /**
   * Eliminar plantilla de un dispositivo
   * @param {string} deviceId - ID del dispositivo
   * @param {string} templateId - ID de la plantilla
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async deleteDeviceTemplate(deviceId, templateId) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${deviceId}/templates/${templateId}`, {
      method: 'DELETE',
    }, token);
  },
};

export default deviceService; 